import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useProducts } from "@/hooks/useProducts";

const field = "h-12 w-full border-0 border-b border-white/20 bg-transparent px-0 text-base text-white placeholder:text-zinc-500 focus:border-[var(--volt)] focus:outline-none focus:ring-0 transition-colors";

export function ContactForm({ defaultKind = "contact", defaultProduct = "", onDone, idPrefix = "contact" }) {
  const { products } = useProducts();
  const [kind, setKind] = useState(defaultKind);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", organization: "", product: defaultProduct, message: "" });

  useEffect(() => {
    setF((x) => ({ ...x, product: defaultProduct }));
    setKind(defaultKind);
  }, [defaultProduct, defaultKind]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, kind });
      toast.success(kind === "quote" ? "Quote request received. We'll be in touch." : "Message sent. We'll be in touch.");
      setF({ name: "", email: "", phone: "", organization: "", product: defaultProduct, message: "" });
      onDone?.();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="space-y-6">
      <div className="flex border border-white/15 w-fit">
        {[["contact", "General"], ["quote", "Quote Request"]].map(([k, l]) => (
          <button
            type="button"
            key={k}
            data-testid={`${idPrefix}-kind-${k}`}
            onClick={() => setKind(k)}
            className={`h-10 px-5 font-mono text-xs uppercase tracking-widest transition-colors ${kind === k ? "bg-volt text-black" : "text-zinc-300 hover:text-white"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <input required data-testid={`${idPrefix}-name-input`} className={field} placeholder="Name *" value={f.name} onChange={set("name")} />
        <input required type="email" data-testid={`${idPrefix}-email-input`} className={field} placeholder="Email *" value={f.email} onChange={set("email")} />
        <input data-testid={`${idPrefix}-phone-input`} className={field} placeholder="Phone" value={f.phone} onChange={set("phone")} />
        <input data-testid={`${idPrefix}-org-input`} className={field} placeholder="Department / Organization" value={f.organization} onChange={set("organization")} />
      </div>
      <select data-testid={`${idPrefix}-product-select`} className={`${field} [&>option]:bg-[#121215]`} value={f.product} onChange={set("product")}>
        <option value="">Product of interest (optional)</option>
        {products.map((p) => (
          <option key={p.slug} value={`${p.name} (${p.model})`}>{p.name} ({p.model})</option>
        ))}
      </select>
      <textarea required data-testid={`${idPrefix}-message-input`} rows={4} className={`${field} h-auto py-3 resize-none`} placeholder="Tell us what you need *" value={f.message} onChange={set("message")} />
      <button type="submit" disabled={busy} data-testid={`${idPrefix}-submit-button`} className="btn-volt h-14 w-full sm:w-auto px-10 font-mono text-sm font-bold uppercase tracking-widest disabled:opacity-50">
        <span className="flex items-center justify-center gap-3">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {kind === "quote" ? "Send Quote Request" : "Send Message"}
        </span>
      </button>
    </form>
  );
}
